import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { toast } from 'sonner';

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('auditor');
  const { login, register } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: 'relative'
    },
    languageSwitcher: {
      position: 'absolute',
      top: '24px',
      right: '24px'
    },
    container: {
      width: '100%',
      maxWidth: '600px',
      padding: '0 24px'
    },
    logoSection: {
      textAlign: 'center',
      marginBottom: '48px'
    },
    logo: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '32px',
      fontWeight: '600',
      color: '#111827'
    },
    shield: {
      fontSize: '36px'
    },
    tabs: {
      display: 'flex',
      marginBottom: '0',
      borderRadius: '12px 12px 0 0',
      overflow: 'hidden',
      border: '1px solid #e5e7eb',
      borderBottom: 'none'
    },
    tab: {
      flex: 1,
      padding: '16px',
      textAlign: 'center',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s'
    },
    tabActive: {
      backgroundColor: '#ffffff',
      color: '#111827'
    },
    tabInactive: {
      backgroundColor: '#f3f4f6',
      color: '#9ca3af'
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '0 0 12px 12px',
      border: '1px solid #e5e7eb',
      padding: '40px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    formTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#111827',
      marginBottom: '8px'
    },
    formSubtitle: {
      fontSize: '15px',
      color: '#6b7280',
      marginBottom: '32px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#111827',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      fontSize: '15px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      marginBottom: '20px',
      boxSizing: 'border-box',
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    select: {
      width: '100%',
      padding: '12px 16px',
      fontSize: '15px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      marginBottom: '20px',
      boxSizing: 'border-box',
      outline: 'none',
      backgroundColor: '#ffffff',
      cursor: 'pointer'
    },
    button: {
      width: '100%',
      padding: '14px',
      fontSize: '16px',
      fontWeight: '500',
      color: '#ffffff',
      backgroundColor: '#111827',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isLogin) {
        await login(email, password);
        toast.success(lang === 'fr' ? 'Connexion réussie !' : 'Login successful!');
      } else {
        await register(name, email, password, role);
        toast.success(lang === 'fr' ? 'Inscription réussie !' : 'Registration successful!');
      }
    } catch (error) {
      toast.error(error.message || (lang === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.languageSwitcher}>
        <LanguageSwitcher />
      </div>

      <div style={styles.container}>
        <div style={styles.logoSection}>
          <div style={styles.logo}>
            <span style={styles.shield}>🛡️</span>
            <span>AuditPlan</span>
          </div>
        </div>

        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              ...(isLogin ? styles.tabActive : styles.tabInactive)
            }}
            onClick={() => setIsLogin(true)}
          >
            Login
          </button>
          <button
            style={{
              ...styles.tab,
              ...(!isLogin ? styles.tabActive : styles.tabInactive)
            }}
            onClick={() => setIsLogin(false)}
          >
            Register
          </button>
        </div>

        <div style={styles.card}>
          <form onSubmit={handleSubmit}>
            <h2 style={styles.formTitle}>
              {isLogin ? 'Login' : 'Register'}
            </h2>
            <p style={styles.formSubtitle}>
              {isLogin 
                ? (lang === 'fr' ? 'Connectez-vous à votre compte AuditPlan' : 'Sign in to your AuditPlan account')
                : (lang === 'fr' ? 'Créez votre compte AuditPlan' : 'Create your AuditPlan account')}
            </p>

            {!isLogin && (
              <>
                <label style={styles.label}>
                  {lang === 'fr' ? 'Nom complet' : 'Full name'}
                </label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder={lang === 'fr' ? 'Votre nom' : 'Your name'}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </>
            )}

            <label style={styles.label}>Email</label>
            <input
              type="email"
              style={styles.input}
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label style={styles.label}>Password</label>
            <input
              type="password"
              style={styles.input}
              placeholder={lang === 'fr' ? 'Votre mot de passe' : 'Your password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            {!isLogin && (
              <>
                <label style={styles.label}>
                  {lang === 'fr' ? 'Je suis un' : 'I am a'}
                </label>
                <select
                  style={styles.select}
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="auditor">{lang === 'fr' ? 'Auditeur' : 'Auditor'}</option>
                  <option value="sponsor">{lang === 'fr' ? 'Sponsor' : 'Sponsor'}</option>
                </select>
              </>
            )}

            <button type="submit" style={styles.button}>
              {isLogin 
                ? (lang === 'fr' ? 'Se connecter' : 'Sign in')
                : (lang === 'fr' ? 'Créer mon compte' : 'Create account')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
